/**
 * Wishlist, Sprint 2.2c.
 *
 * Customer-facing /wishlist page. Authed-only. Renders saved SKUs as
 * a responsive grid. Each card has:
 *   - Image / category-coloured placeholder
 *   - Vendor + product name + price
 *   - Saved-on date
 *   - Two CTAs: "Open in chat" (deep-link with brief prefilled) and
 *     "Remove" (unsaves)
 *
 * Empty state nudges the customer back to /chat or /browse where
 * the heart-toggle lives.
 *
 * Anon visitors hit the auth gate (the route is wrapped in
 * ProtectedRoute in App.tsx), so the page itself doesn't need to
 * handle the unauthed branch.
 */

import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import { Heart, ArrowRight, Trash2, Loader2, Sparkles, MessageCircle } from "lucide-react";
import { PageHeader } from "@/components/lembra/PageHeader";
import { PageShell } from "@/components/lembra/PageShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ErrorCard } from "@/components/lembra/ErrorCard";
import { useAuth } from "@/hooks/useAuth";
import {
  useWishlist,
  useWishlistMutations,
  type WishlistRow,
} from "@/hooks/queries";
// MS64a: route saved gifts through the canonical product detail
// modal instead of bouncing to /chat. The "Talk it through" CTA
// remains as a secondary affordance for users who want chat
// context rather than the order flow.
import { useOpenAuthedConcierge } from "@/hooks/useOpenAuthedConcierge";
import { ProductCheckoutModals } from "@/components/product-detail/ProductCheckoutModals";
import { useProductCheckoutFlow } from "@/hooks/useProductCheckoutFlow";
import { wishlistRowToSkuCardData } from "@/lib/wishlistRowToSkuCardData";
import { getOrCreateGuestSessionId } from "@/lib/guestSession";
import { formatSkuName } from "@/lib/formatSkuName"; // MS407: for consistent voucher/prefix stripping on saved cards

const CATEGORY_GRADIENTS: Record<string, string> = {
  cake:       "from-butter to-butter/40",
  hamper:     "from-coral/30 to-butter",
  florist:    "from-coral/20 to-butter/60",
  workshop:   "from-navy/10 to-butter/60",
  spa:        "from-butter/80 to-coral/15",
  experience: "from-navy/8 to-coral/15",
  meal:       "from-butter/60 to-coral/20",
};

function gradientFor(category: string): string {
  return CATEGORY_GRADIENTS[category.toLowerCase()] ?? "from-butter to-butter/40";
}

export default function Wishlist() {
  const navigate = useNavigate();
  const { openFromChatUrl, openGeneric } = useOpenAuthedConcierge();
  const { user } = useAuth();
  const wishlistQuery = useWishlist(user?.id);
  const { add, remove } = useWishlistMutations(user?.id);

  // MS64a — two-stage modal flow (same pattern as Chat / Browse /
  // Vendor): tile "View" click opens ProductDetailModal; modal's
  // "Send to recipient" CTA opens OrderConfirmSheet. Authed page so
  // isAnonymous=false → the order sheet skips email capture and
  // pre-fills from session.
  const checkout = useProductCheckoutFlow({ cameFrom: "wishlist" });
  // Guest session id is null for authed users; OrderConfirmSheet
  // takes it as a prop and ignores when isAnonymous=false. Kept
  // for prop-shape consistency.
  const isAnon = !user;
  const guestSessionId = useMemo(
    () => (isAnon ? getOrCreateGuestSessionId() : null),
    [isAnon],
  );

  if (wishlistQuery.isError) {
    // MS-batch3: the error branch used to return a bare card with no
    // PageShell chrome and no way to recover. Wrap it in the same
    // PageShell + PageHeader as the happy path so the page doesn't
    // collapse to the viewport edge, and give ErrorCard an onRetry that
    // refetches — matching how Team / Notifications handle query errors.
    return (
      <PageShell width="wide">
        <PageHeader
          title="Saved gifts"
          emphasis="Saved"
          subtitle="Your saved gifts will land here."
        />
        <ErrorCard
          title="Couldn't load your saved gifts"
          message="Give it another go in a moment, or check your connection."
          onRetry={() => wishlistQuery.refetch()}
        />
      </PageShell>
    );
  }

  const rows = wishlistQuery.data ?? [];
  const loading = wishlistQuery.isLoading;

  return (
    // MS253: PageShell for Wishlist breathing room.
    // MS585: promoted default (1024px) → wide (1152px) per Phase 4
    // large-screen layout discovery brief §7. Wishlist renders the same
    // product-tile vocabulary as Browse (cards in a 2/3-col grid). The
    // brief recommends letting the grid breathe at 1440+; `wide` is the
    // largest capped variant available without going `full`. Tiles
    // remain legible at 1920px (3-col layout fills the 1152px cap +
    // 32-48px gutters) without crowding.
    <PageShell width="wide">
      {/* MS274: header + CTA was wrapped in a custom `flex items-center
          justify-between` which forced the row to stay side-by-side on
          mobile, squishing the headline. PageHeader has a `tail` prop
          for exactly this — it stacks the CTA below on mobile and
          floats it to the right on sm:+ via its own internal
          flex-col → sm:flex-row responsive layout. */}
      {/* MS391: naming sweep — eyebrow was "Wishlist" while the
          sidebar nav, empty-state copy, and chat agent's reply
          phrasing all say "Saved". One concept, one label. URL
          stays /wishlist + DB table stays `wishlists` (back-compat
          + internal naming is allowed to diverge from user-facing). */}
      <PageHeader
        title="Saved gifts"
        emphasis="Saved"
        subtitle={
          loading
            ? "Loading…"
            : rows.length === 0
              ? "Your saved gifts will land here."
              : `${rows.length} saved gift${rows.length === 1 ? "" : "s"} · newest first.`
        }
        tail={
          rows.length > 0 ? (
            // MS-batch3: was "Plan a gift" here while the empty-state CTA
            // for the same openGeneric action said "Open chat". One verb
            // for one action.
            <Button variant="outline" onClick={openGeneric}>
              <Sparkles className="mr-1.5 h-4 w-4" />
              Open chat
            </Button>
          ) : undefined
        }
      />

      {loading ? (
        <div
          className="flex items-center justify-center py-20 text-charcoal/55"
          aria-live="polite"
          aria-label="Loading saved gifts"
        >
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-coral/10">
            <Heart className="h-6 w-6 text-coral" />
          </div>
          <h2
            className="mt-4"
            style={{
              fontFamily: "Newsreader, Georgia, serif",
              fontWeight: 500,
              fontSize: "1.4rem",
              lineHeight: 1.15,
              letterSpacing: "-0.01em",
              color: "#1D3461",
            }}
          >
            No saved gifts{" "}
            <em
              style={{
                fontStyle: "italic",
                fontWeight: 500,
                color: "#C4564A",
              }}
            >
              yet
            </em>
          </h2>
          <p className="mt-2 text-sm text-charcoal/65">
            Tap the heart on any recommendation in chat or browse to save it for later.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button variant="primary" onClick={openGeneric}>
              Open chat
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={() => navigate("/browse")}>
              Browse gifts
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((row) => (
            <SavedGiftCard
              key={row.id}
              row={row}
              isRemoving={remove.isPending && remove.variables === row.sku_id}
              onRemove={() => {
                if (!row.sku) return;
                // MS100: toast feedback + Undo on remove, mirrors the
                // SkuCard heart-toggle pattern (MS93). Pre-MS100 the
                // card just silently disappeared; users frequently
                // reported "did I just click remove?" because there
                // was no confirmation. Undo re-adds the same sku_id
                // via the mutation we already have.
                const skuId = row.sku_id;
                const productName = row.sku?.name ?? "this gift";
                remove.mutate(skuId);
                toast("Removed from saved", {
                  description: `${productName} is no longer in your saved gifts.`,
                  action: {
                    label: "Undo",
                    onClick: () => add.mutate(skuId),
                  },
                });
              }}
              onView={() => {
                // MS64a: primary CTA now opens the canonical
                // ProductDetailModal — the rich product surface
                // with gallery / long_description / voucher terms
                // / variant picker / etc. Buyer reviews then
                // clicks "Send to recipient" inside the modal to
                // open OrderConfirmSheet.
                const skuCardData = wishlistRowToSkuCardData(row);
                if (skuCardData) checkout.handleViewCard(skuCardData);
              }}
              onTalkItThrough={() => {
                // Secondary CTA: the legacy "deep-link into chat
                // with a brief about this SKU" path. Kept for users
                // who want concierge advice / alternatives rather
                // than going straight to the order form.
                if (!row.sku) return;
                const brief = `I'm thinking about ${row.sku.name} from ${row.sku.vendor_name}.`;
                openFromChatUrl(
                  `/chat?brief=${encodeURIComponent(brief)}` +
                    `&category=${encodeURIComponent(row.sku.vendor_category)}` +
                    `&sku_id=${encodeURIComponent(row.sku.id)}`,
                );
              }}
            />
          ))}
        </div>
      )}

      <ProductCheckoutModals
        flow={checkout}
        isAnonymous={isAnon}
        guestSessionId={guestSessionId}
      />
    </PageShell>
  );
}

function SavedGiftCard({
  row,
  isRemoving,
  onRemove,
  onView,
  onTalkItThrough,
}: {
  row: WishlistRow;
  isRemoving: boolean;
  onRemove: () => void;
  /** MS64a: opens ProductDetailModal — the canonical product
   *  surface with gallery / long_description / voucher_terms /
   *  variant picker. */
  onView: () => void;
  /** MS64a: secondary CTA — opens /chat with the SKU as anchor
   *  context. Kept for users who want concierge advice rather
   *  than the order flow. */
  onTalkItThrough: () => void;
}) {
  const sku = row.sku;
  // Sprint 2.5a B.2 P2-1, single date format. Recent saves get the
  // friendlier relative form ("3 days ago"); anything older than ~2
  // weeks switches to the absolute date so distant timestamps don't
  // read as "8 months ago" with no anchor. The dual-format ("3 days
  // ago · 11 May 2026") was visually noisy on every card; one form
  // at a time per audit.
  const savedAtLabel = (() => {
    try {
      const date = parseISO(row.created_at);
      const ageDays = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
      return ageDays <= 14
        ? formatDistanceToNow(date, { addSuffix: true })
        : format(date, "d MMM yyyy");
    } catch {
      return null;
    }
  })();

  // Vendor_product was deleted between save and read. Render a stub so
  // the user can clean up the stale row without the page breaking.
  if (!sku) {
    return (
      <Card className="overflow-hidden p-5 text-center opacity-70">
        <p className="text-sm font-semibold text-charcoal/60">Item no longer available</p>
        <p className="mt-1 text-xs text-charcoal/45">
          This gift isn't available anymore. You saved it {savedAtLabel ?? "earlier"}.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-4"
          disabled={isRemoving}
          onClick={onRemove}
        >
          {/* MS-batch3: icon was abutting the "Remove" label with no gap
              (unlike the live card's spaced icons). Add mr-1.5 to match. */}
          {isRemoving ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
          )}
          Remove
        </Button>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col overflow-hidden p-0">
      {/* Image / placeholder */}
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-charcoal/5">
        {sku.image_url ? (
          <img
            src={sku.image_url}
            alt={`${sku.name}, ${sku.vendor_category} from ${sku.vendor_name}`}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div
            role="img"
            aria-label={`${sku.name}, ${sku.vendor_category} from ${sku.vendor_name}`}
            className={`h-full w-full bg-gradient-to-br ${gradientFor(sku.vendor_category)} flex items-end p-3`}
          >
            <span
              className="text-xs font-medium uppercase tracking-wider text-navy/55"
              style={{ letterSpacing: "0.1em" }}
            >
              {sku.vendor_category}
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-4">
        <p className="text-xs text-charcoal/55">{sku.vendor_name}</p>
        <h3 className="mt-0.5 line-clamp-2 text-sm font-semibold leading-tight text-navy">
          {/* MS407: formatSkuName strips residual vendor-name prefixes + voucher tokens */}
          {formatSkuName({ name: sku.name, vendor_name: sku.vendor_name, vendor_category: sku.vendor_category, is_voucher: sku.is_voucher ?? undefined })}
        </h3>
        <p className="mt-1 text-sm font-semibold text-navy">
          ${Number(sku.price ?? 0).toFixed(0)}
          {sku.is_voucher ? " voucher" : ""}
        </p>
        <p className="mt-2 text-[11px] text-charcoal/55">
          Saved {savedAtLabel ?? "earlier"}
        </p>

        {/* Actions row: View (primary) + Talk it through (secondary)
            + Remove. MS64a — View opens ProductDetailModal which is
            the canonical product surface with gallery + long_desc
            + voucher terms + variant picker. Talk it through stays
            as the legacy chat deep-link for users who want concierge
            advice. */}
        <div className="mt-4 flex flex-col gap-2">
          <Button
            variant="primary"
            size="sm"
            className="w-full justify-center"
            onClick={onView}
          >
            View
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 justify-center"
              onClick={onTalkItThrough}
              aria-label={`Talk through ${sku.name} with the concierge`}
            >
              <MessageCircle className="mr-1 h-3.5 w-3.5" />
              Talk it through
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={isRemoving}
              onClick={onRemove}
              aria-label={`Remove ${sku.name} from saved`}
              title="Remove from saved"
            >
              {isRemoving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
